import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { COMPANY } from "../data/company";
import SEO from "../components/SEO";
import ContactForm from "../components/ContactForm";

export default function Contact() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash !== "#kontaktformular") return;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById("kontaktformular")?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
        block: "start",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.key, location.hash]);

  return (
    <>
      <SEO
        title="Kontakt"
        description={`Kontakt ${COMPANY.name} i ${COMPANY.city}. Ring på ${COMPANY.phone} eller send en email til ${COMPANY.email} for et uforpligtende tilbud på tømrerarbejde.`}
        path="/kontakt"
        image="/_assets/hero-toemrer-arbejde.jpg"
      />

      {/* Header */}
      <section className="bg-[#111111] text-white py-16 sm:py-20 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-[#A78E51] font-bold text-xs uppercase tracking-widest mb-3">
              <Link to="/" className="hover:underline">Forside</Link>
              <i className="fa-solid fa-angle-right text-[10px] text-neutral-500" aria-hidden="true" />
              <span>Kontakt</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Kontakt Andreas Guldmann
            </h1>
            <p className="mt-4 text-base sm:text-lg text-neutral-300 leading-relaxed">
              Har du et projekt inden for døre & vinduer, træterrasser eller indendørs renovering af lofter og vægge? Vi står klar til at besvare dine spørgsmål og udarbejde et uforpligtende tilbud.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content Grid */}
      <section className="py-16 sm:py-24 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Info Col */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-[#A78E51] font-bold text-xs uppercase tracking-widest block mb-1">
                  Kontaktoplysninger
                </span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#111111] tracking-tight">
                  Vi er klar til at hjælpe
                </h2>
                <p className="mt-3 text-neutral-600 text-sm leading-relaxed">
                  Du er altid velkommen til at ringe direkte, sende en email eller udfylde kontaktformularen. Vi besvarer henvendelser hurtigst muligt.
                </p>
              </div>

              {/* Contact cards */}
              <div className="space-y-4 pt-2">
                {/* Telefon */}
                <a
                  href={COMPANY.phoneLink}
                  className="flex items-start gap-4 p-5 rounded-lg bg-white border border-neutral-200 hover:border-[#A78E51] transition-colors shadow-sm group"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#111111] text-[#A78E51] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-phone text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-neutral-500 font-semibold uppercase block">
                      Telefon (klik for at ringe)
                    </span>
                    <span className="text-lg font-bold text-[#111111] group-hover:text-[#A78E51] transition-colors whitespace-nowrap">
                      {COMPANY.phone}
                    </span>
                    <span className="text-xs text-neutral-500 block mt-0.5">
                      Direkte kontakt til Andreas
                    </span>
                  </div>
                </a>

                {/* Email */}
                <a
                  href={COMPANY.emailLink}
                  className="flex items-start gap-4 p-5 rounded-lg bg-white border border-neutral-200 hover:border-[#A78E51] transition-colors shadow-sm group"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#111111] text-[#A78E51] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-envelope text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-neutral-500 font-semibold uppercase block">
                      E-mail (klik for at sende mail)
                    </span>
                    <span className="text-base font-bold text-[#111111] group-hover:text-[#A78E51] transition-colors whitespace-nowrap">
                      {COMPANY.email}
                    </span>
                    <span className="text-xs text-neutral-500 block mt-0.5">
                      Send din beskrivelse eller billeder
                    </span>
                  </div>
                </a>

                {/* Adresse */}
                <div className="flex items-start gap-4 p-5 rounded-lg bg-white border border-neutral-200 shadow-sm">
                  <div className="w-12 h-12 rounded-lg bg-[#111111] text-[#A78E51] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-location-dot text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-neutral-500 font-semibold uppercase block">
                      Adresse
                    </span>
                    <span className="text-base font-bold text-[#111111] block">
                      {COMPANY.name}
                    </span>
                    <span className="text-sm text-neutral-700 block">
                      {COMPANY.address}
                    </span>
                    <span className="text-xs text-neutral-500 block mt-1">
                      CVR: {COMPANY.cvr}
                    </span>
                  </div>
                </div>

                {/* Facebook */}
                <a
                  href={COMPANY.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 rounded-lg bg-white border border-neutral-200 hover:border-[#A78E51] transition-colors shadow-sm group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-[#1877F2]/10 text-[#1877F2] flex items-center justify-center text-lg">
                      <i className="fa-brands fa-facebook-f" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-sm font-bold text-[#111111] group-hover:text-[#A78E51] block">
                        Følg os på Facebook
                      </span>
                      <span className="text-xs text-neutral-500">
                        Opdateringer og billeder fra hverdagen
                      </span>
                    </div>
                  </div>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs text-neutral-400 group-hover:text-[#A78E51] transition-colors" aria-hidden="true" />
                </a>
              </div>
            </div>

            {/* Form Col */}
            <div id="kontaktformular" className="lg:col-span-7 scroll-mt-36">
              <div className="mb-6">
                <span className="text-[#A78E51] font-bold text-xs uppercase tracking-widest block mb-1">
                  Send en besked
                </span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#111111] tracking-tight">
                  Udfyld kontaktformularen
                </h2>
                <p className="mt-2 text-neutral-600 text-sm">
                  Skriv om din opgave, så vender Andreas tilbage med et svar eller et opkald.
                </p>
              </div>

              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
